/**
 * Infinite-scroll hook for catalog listings.
 *
 * Contract (matches Stage 4 requirements):
 *   1. Preserves active filters (`category`, `genre`, `q`) across pagination.
 *   2. Shows a visible loading state while the next page is in flight.
 *   3. Stops requesting when the last response was shorter than `pageSize`.
 *   4. Prevents duplicate tracks (dedup by id) and races (guarded by an
 *      incrementing requestId ref).
 *   5. Resets pagination to page 1 whenever any filter changes.
 *   6. Exposes a `sentinelRef` — attach it to a DOM node at the end of the
 *      grid; when it intersects the viewport the next page loads.
 *
 * NOTE: The API contract is `GET /api/tracks?category=&genre=&q=&skip=&limit=`.
 * `limit` acts as the page size; `skip` advances by that amount per page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEFAULT_PAGE_SIZE = 24;

/**
 * @param {{category?:string, genre?:string, q?:string, section?:string}} filters
 * @param {{pageSize?:number, initialTracks?:Array}} [options]
 */
export default function useInfiniteTracks(filters, options = {}) {
  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE;
  const [tracks, setTracks] = useState(options.initialTracks || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  // Guard against out-of-order responses when filters change mid-flight.
  const requestIdRef = useRef(0);
  // Track which page we're on. Reset to 0 on filter change.
  const pageRef = useRef(0);

  const cacheKey = useMemo(() => JSON.stringify({
    category: filters.category || "",
    genre: filters.genre || "",
    q: filters.q || "",
    section: filters.section || "",
    trackType: filters.trackType || "",
    editCategory: filters.edit_category || "",
    userKey: filters.userKey || "",
    attrsQs: filters.attrsQs || "",
  }), [filters.category, filters.genre, filters.q, filters.section, filters.trackType, filters.edit_category, filters.userKey, filters.attrsQs]);

  const buildParams = useCallback((page) => {
    const params = new URLSearchParams();
    // `section` is used by /music/{section} pages — same as `category` but
    // set from the URL. Section overrides the free-form category chip when
    // both are provided (e.g. a locked-category section page).
    const category = filters.section || filters.category || "";
    if (category) params.set("category", category);
    if (filters.genre) params.set("genre", filters.genre);
    if (filters.q) params.set("q", filters.q);
    if (filters.trackType) params.set("track_type", filters.trackType);
    if (filters.edit_category) params.set("edit_category", filters.edit_category);
    new URLSearchParams(filters.attrsQs || "").forEach((v, k) => params.set(k, v));
    params.set("limit", String(pageSize));
    params.set("skip", String(page * pageSize));
    return params.toString();
  }, [filters.category, filters.section, filters.genre, filters.q, filters.trackType, filters.edit_category, filters.attrsQs, pageSize]);

  /** Fetch a specific page. `append=true` for infinite-scroll pagination. */
  const fetchPage = useCallback(async (page, append) => {
    const myRequestId = ++requestIdRef.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError(null);
    try {
      const qs = buildParams(page);
      const res = await api.get(`/tracks?${qs}`);
      // Discard stale responses (a newer request has been fired).
      if (myRequestId !== requestIdRef.current) return;
      const rows = Array.isArray(res.data) ? res.data : [];
      // Dedupe against existing tracks by id
      setTracks((prev) => {
        if (!append) return rows;
        const seen = new Set(prev.map((t) => t.id));
        const additions = rows.filter((r) => r.id && !seen.has(r.id));
        return prev.concat(additions);
      });
      // No more pages when the API returns fewer than `pageSize` rows.
      setHasMore(rows.length >= pageSize);
      pageRef.current = page;
    } catch (e) {
      if (myRequestId === requestIdRef.current) {
        setError(e?.response?.data?.detail || "Failed to load tracks.");
        setHasMore(false);
      }
    } finally {
      if (myRequestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [buildParams, pageSize]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    fetchPage(pageRef.current + 1, true);
  }, [hasMore, isLoading, isLoadingMore, fetchPage]);

  const reload = useCallback(() => {
    pageRef.current = 0;
    setHasMore(true);
    fetchPage(0, false);
  }, [fetchPage]);

  // Reset + fetch page 0 on any filter change.
  const enabled = options.enabled !== false;
  useEffect(() => {
    pageRef.current = 0;
    setTracks([]);
    setHasMore(enabled);
    if (enabled) fetchPage(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  // IntersectionObserver on the sentinel node.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root: null, rootMargin: "300px", threshold: 0 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore]);

  return {
    tracks,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    reload,
    sentinelRef,
  };
}
